/**
 * Crews grid — list and management of installation crews for project services.
 *
 * Server-backed via EntityGrid over odl-app/crews/. Columns come from the
 * backend grid-spec descriptor; FE field overrides preserve the active-status
 * chip and crew-members display. Status filter drives filter[active] via
 * extraParams. Add/Edit crew dialog is preserved and wired to refresh the
 * grid after mutation.
 *
 * Contains:
 * - CrewFormDialog — create/edit a crew with member management and extra-pay config
 * - CrewsTable     — EntityGrid over odl-app/crews/ (default export)
 */

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import {
  Box,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useFormik } from 'formik';
import { produce } from 'immer';
import _ from 'lodash';
import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import * as yup from 'yup';

import FilterBar from '@sophocles/components/FilterBar';
import ResponsiveDialog from '@sophocles/components/ResponsiveDialog';
import UserAutoComplete from '@sophocles/components/UserAutoComplete';
import UserAvatar from '@sophocles/components/UserAvatar';
import { EntityGrid } from '@sophocles/data-grids';
import GridStatusCell from '@sophocles/data-grids/components/cells/GridStatusCell';
import { useGridSessionKey } from '@sophocles/data-grids/datasource/useGridPreferenceKey';
import useGridRefreshRef from '@sophocles/data-grids/datasource/useGridRefreshRef';
import {
  useCreateCrewMutation,
  useLazyListCrewExtraPayQuery,
  usePatchCrewMutation,
} from '@sophocles/features/entities/crews';
import { useListCrewMemberUsersQuery } from '@sophocles/features/entities/users';
import { validateCrewNumber } from '@sophocles/features/projectServices';
import { addToast } from '@sophocles/features/toast';
import usePersistedState from '@sophocles/hooks/usePersistedState';
import { parseApiError } from '@sophocles/utils/apiErrors';
import { crewMemberName, hydrateCrewMembers } from '@sophocles/views/services/utils/crewMembers';

const MaxUsers = 5;

function CrewFormDialog({ data, open, onClose, onSubmit }) {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [createCrew] = useCreateCrewMutation();
  const [patchCrew] = usePatchCrewMutation();
  const [fetchCrewExtraPay] = useLazyListCrewExtraPayQuery();

  const crewId = _.get(data, 'id', null);

  const [crewNumber, setCrewNumber] = useState(_.get(data, 'crew_number', null));
  const [users, setUsers] = useState(_.get(data, 'users', []));
  // The picker's directory is only needed while the dialog is open.
  const { data: crewMemberUsers } = useListCrewMemberUsersQuery(undefined, { skip: !open });
  const technicianUsers = useMemo(() => crewMemberUsers ?? [], [crewMemberUsers]);
  const [crewExtraPay, setCrewExtraPay] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEditingCrew = crewNumber != null;

  // The crew list sends members as references, so the picker's directory is
  // what supplies a member's email, customer, and avatar once it has loaded.
  const memberDetails = useMemo(() => hydrateCrewMembers(users, technicianUsers), [users, technicianUsers]);

  const CrewFormValidationSchema = useMemo(
    () =>
      yup.object({
        crew_number: yup
          .number()
          .required(t('projectServices.crews.validation.crewNumberRequired'))
          .positive(t('projectServices.crews.validation.crewNumberPositive'))
          .integer(t('projectServices.crews.validation.crewNumberInteger')),
      }),
    [t],
  );

  const handleSubmit = async values => {
    if (!validateCrewNumber(values.crew_number)) {
      dispatch(
        addToast({
          title: t('projectServices.crews.toast.validationError'),
          message: t('projectServices.crews.toast.invalidCrewNumber'),
          level: 'error',
          dismissible: true,
        }),
      );
      return;
    }

    setIsSubmitting(true);

    try {
      const body = {
        active: true,
        ...values,
        user_ids: users.map(user => user.id),
        crew_member_pay: users.map(user => ({
          member: user.id,
          extra_pay: crewExtraPay[user.id]?.extra_pay ?? 0,
        })),
      };

      const crewResult = crewId ? await patchCrew({ id: crewId, body }) : await createCrew({ body });

      if (crewResult.error) {
        // Surfaces crew-number validation and the member-pay 403 alike.
        const { nonFieldMessages } = parseApiError(crewResult.error, {
          knownFields: [],
          genericMessage: t('projectServices.crews.toast.genericError'),
        });

        dispatch(
          addToast({
            title: t('projectServices.crews.toast.error'),
            message: t(
              isEditingCrew ? 'projectServices.crews.toast.modifyFailed' : 'projectServices.crews.toast.createFailed',
              { errors: nonFieldMessages.map(x => '* ' + x).join('; ') },
            ),
            level: 'error',
            dismissible: true,
          }),
        );
        return;
      }

      dispatch(
        addToast({
          title: t('projectServices.crews.toast.success'),
          message: t(
            isEditingCrew ? 'projectServices.crews.toast.updateSuccess' : 'projectServices.crews.toast.createSuccess',
          ),
          level: 'success',
          dismissible: true,
        }),
      );

      if (onSubmit) {
        onSubmit(body);
      }
    } catch (error) {
      dispatch(
        addToast({
          title: t('projectServices.crews.toast.error'),
          message: t('projectServices.crews.toast.unexpectedError'),
          level: 'error',
          dismissible: true,
        }),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      crew_number: crewNumber || '',
    },
    validationSchema: CrewFormValidationSchema,
    onSubmit: values => {
      handleSubmit(values);
    },
  });

  const handleClose = () => {
    formik.resetForm();
    setIsSubmitting(false);
    if (onClose) {
      onClose();
    }
  };

  const handleAddNewUser = () => {
    if (users.length >= MaxUsers) return;

    setUsers(
      produce(users, draft => {
        draft.push(null);
        return draft;
      }),
    );
  };

  const handleDeleteUser = index => {
    setUsers(
      produce(users, draft => {
        draft.splice(index, 1);
        return draft;
      }),
    );
  };

  const handleCrewMemberPayChange = (user, value) => {
    const numericValue = parseFloat(value) || 0;

    setCrewExtraPay(
      produce(crewExtraPay, draft => {
        if (!draft[user.id]) {
          draft[user.id] = { id: null, extra_pay: numericValue };
        } else {
          draft[user.id].extra_pay = numericValue;
        }
        return draft;
      }),
    );
  };

  const handleUserSelect = (index, selectedUser) => {
    setUsers(
      produce(users, draft => {
        draft[index] = selectedUser;
        return draft;
      }),
    );

    // Initialize extra pay for new user
    if (selectedUser && !crewExtraPay[selectedUser.id]) {
      setCrewExtraPay(
        produce(crewExtraPay, draft => {
          draft[selectedUser.id] = { id: null, extra_pay: 0 };
          return draft;
        }),
      );
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetchCrewExtraPay({ crewId });
        const extraPayData = response.data?.results || [];

        const newExtraPay = extraPayData.reduce((acc, item) => {
          acc[item.member] = {
            id: item.id,
            extra_pay: parseFloat(item.extra_pay) || 0,
          };
          return acc;
        }, {});

        setCrewExtraPay(newExtraPay);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    if (open && crewId) {
      fetchData();
    }
  }, [fetchCrewExtraPay, crewId, open]);

  useEffect(() => {
    setCrewNumber(_.get(data, 'crew_number', null));
    setUsers(_.get(data, 'users', []));
  }, [data, open]);

  const availableUsers = technicianUsers.filter(user => {
    const selectedUserIds = users.map(u => u?.id).filter(Boolean);
    return !selectedUserIds.includes(user.id);
  });

  const canSubmit =
    !isSubmitting &&
    !formik.isSubmitting &&
    Object.keys(formik.errors).length === 0 &&
    users.length > 0 &&
    !users.some(user => user == null);

  return (
    <ResponsiveDialog
      title={isEditingCrew ? t('projectServices.crews.dialog.editTitle') : t('projectServices.crews.dialog.newTitle')}
      open={open}
      canSubmit={canSubmit}
      onClose={handleClose}
      onSubmit={formik.submitForm}
      sx={{
        '& .MuiDialogContent-root > :not(style):not(:last-child)': { mb: 2 },
        '& .MuiDialogContent-root': { py: 3 },
      }}
    >
      <TextField
        required
        fullWidth
        name="crew_number"
        label={t('projectServices.crews.form.crewNumber')}
        type="number"
        size="small"
        value={formik.values.crew_number}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={formik.touched.crew_number && Boolean(formik.errors.crew_number)}
        helperText={formik.touched.crew_number && formik.errors.crew_number}
        disabled={isSubmitting}
      />

      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">
            {t('projectServices.crews.form.usersCount', { count: users.length, max: MaxUsers })}
          </Typography>
          <IconButton disabled={users.length >= MaxUsers || isSubmitting} onClick={handleAddNewUser}>
            <AddIcon />
          </IconButton>
        </Box>

        <List dense>
          {memberDetails.map((user, index) => (
            <ListItem
              key={index}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 'var(--radius-md)',
                mb: 2,
                py: 1,
                opacity: isSubmitting ? 0.6 : 1,
              }}
              secondaryAction={
                <IconButton
                  edge="end"
                  aria-label={t('actions.delete')}
                  onClick={() => handleDeleteUser(index)}
                  disabled={isSubmitting}
                >
                  <DeleteIcon />
                </IconButton>
              }
            >
              <ListItemAvatar>
                <UserAvatar user={user} size={40} />
              </ListItemAvatar>

              {user ? (
                <ListItemText
                  primary={crewMemberName(user)}
                  secondary={
                    <Fragment>
                      <Typography variant="caption" display="block">
                        {user.customer?.name}
                      </Typography>
                      <TextField
                        fullWidth
                        type="number"
                        size="small"
                        name={`user_${index}_extra_pay`}
                        label={t('projectServices.crews.form.extraPayRate')}
                        helperText={t('projectServices.crews.form.perFixtureInstall')}
                        value={_.get(crewExtraPay[user.id], 'extra_pay', 0)}
                        onChange={e => handleCrewMemberPayChange(user, e.target.value)}
                        disabled={isSubmitting}
                        inputProps={{
                          min: 0,
                          step: 0.01,
                        }}
                        sx={{ mt: 1 }}
                      />
                    </Fragment>
                  }
                />
              ) : (
                <UserAutoComplete
                  fullWidth
                  users={availableUsers}
                  sx={{ flex: 1 }}
                  disabled={isSubmitting}
                  onChange={e => {
                    const selectedUser = _.find(technicianUsers, u => u.id === e.target.value);
                    handleUserSelect(index, selectedUser);
                  }}
                />
              )}
            </ListItem>
          ))}
        </List>
      </Box>
    </ResponsiveDialog>
  );
}

function CrewsTable({ customerId }) {
  const { t } = useTranslation();

  const { onGridReady: handleGridReady, refresh: refreshGrid } = useGridRefreshRef();
  const [isFormDialogOpen, setIsFormDialogOpen] = useState(false);
  const [selectedCrew, setSelectedCrew] = useState({});
  const [statusFilter, setStatusFilter] = usePersistedState(useGridSessionKey('crewsTable', 'statusFilter'), 'active');

  const STATUS_OPTIONS = useMemo(
    () => [
      { label: t('projectServices.crews.status.active'), value: 'active' },
      { label: t('projectServices.crews.status.inactive'), value: 'inactive' },
    ],
    [t],
  );

  const extraParams = useMemo(() => {
    const params = {};
    if (customerId) params.customer_id = customerId;
    if (statusFilter === 'active') params.active = true;
    else if (statusFilter === 'inactive') params.active = false;
    return params;
  }, [customerId, statusFilter]);

  const handleStatusFilterChange = useCallback(
    value => {
      setStatusFilter(value);
    },
    [setStatusFilter],
  );

  const handleFormSubmit = useCallback(() => {
    setIsFormDialogOpen(false);
    refreshGrid();
  }, [refreshGrid]);

  const fieldOverrides = useMemo(
    () => ({
      active: {
        renderCell: ({ value }) => (
          <GridStatusCell
            color={value ? 'ok' : 'neutral'}
            label={value ? t('projectServices.crews.status.active') : t('projectServices.crews.status.inactive')}
          />
        ),
      },
      users: {
        valueGetter: value => {
          if (!value || value.length === 0) return t('projectServices.crews.noMembers');
          return _.sortBy(value, crewMemberName).map(crewMemberName).join(', ');
        },
      },
    }),
    [t],
  );

  // Edit action — not modeled in the descriptor
  const extraColumns = useMemo(
    () => [
      {
        field: 'actions',
        headerName: t('projectServices.crews.columns.action'),
        width: 80,
        sortable: false,
        filterable: false,
        hideable: false,
        disableColumnMenu: true,
        align: 'right',
        headerAlign: 'right',
        display: 'flex',
        renderCell: ({ row }) => (
          <Tooltip title={t('projectServices.crews.tooltip.editCrew')}>
            <IconButton
              color="primary"
              onClick={e => {
                e.stopPropagation();
                setSelectedCrew(row);
                setIsFormDialogOpen(true);
              }}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
        ),
      },
    ],
    [t],
  );

  const quickFilters = useMemo(
    () => [
      {
        key: 'status',
        label: t('general.status'),
        type: 'single',
        allLabel: t('projectServices.crews.filter.allCrews'),
        options: STATUS_OPTIONS,
        value: statusFilter,
        onChange: value => handleStatusFilterChange(value ?? null),
      },
    ],
    [t, statusFilter, handleStatusFilterChange],
  );

  const handleResetAll = useCallback(() => handleStatusFilterChange(null), [handleStatusFilterChange]);

  const toolbarContent = <FilterBar quickFilters={quickFilters} onResetAll={handleResetAll} />;

  return (
    <Fragment>
      <EntityGrid
        basePath="crews"
        fieldOverrides={fieldOverrides}
        extraParams={extraParams}
        options={{
          searchPlaceholder: t('projectServices.crews.filter.allCrews'),
          persistKey: 'crewsTable',
          toolbarContent,
          toolbarVariant: 'flat',
          createAction: {
            label: t('projectServices.crews.dialog.newTitle'),
            onClick: () => {
              setSelectedCrew({});
              setIsFormDialogOpen(true);
            },
          },
          extraColumns,
          onGridReady: handleGridReady,
          countNoun: t('projectServices.crews.countNoun', 'crews'),
        }}
      />

      <CrewFormDialog
        data={selectedCrew}
        open={isFormDialogOpen}
        onClose={() => setIsFormDialogOpen(false)}
        onSubmit={handleFormSubmit}
      />
    </Fragment>
  );
}

export default CrewsTable;
